import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import StarRating from "@/components/StarRating";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const ProductReviews = ({ productId, onStatsChange }) => {
  const { lang, t } = useLang();
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ avg_rating: 0, review_count: 0 });
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ rating: 5, title: "", comment: "", customer_name: "" });

  const load = async () => {
    try {
      const res = await api.get(`/products/${productId}/reviews`);
      setReviews(res.data.reviews || []);
      const s = { avg_rating: res.data.avg_rating || 0, review_count: res.data.review_count || 0 };
      setStats(s);
      if (onStatsChange) onStatsChange(s);
    } catch {}
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, customer_name: f.customer_name || user.name }));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.comment.trim() || form.comment.trim().length < 3) {
      toast.error(t("reviews.commentRequired"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/products/${productId}/reviews`, {
        rating: form.rating,
        title: form.title || null,
        comment: form.comment,
        customer_name: form.customer_name || null,
      });
      toast.success(t("reviews.submitSuccess"));
      setForm({ rating: 5, title: "", comment: "", customer_name: user?.name || "" });
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="border-t border-[#EBE5DF] mt-16 pt-16" data-testid="product-reviews">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <p className="overline mb-3">{t("reviews.section")}</p>
          <h2 className="font-serif text-3xl lg:text-4xl">{t("reviews.title")}</h2>
          <div className="flex items-center gap-3 mt-3">
            <StarRating value={stats.avg_rating} size="md" />
            <span className="text-sm text-[#736B66]">
              {stats.avg_rating > 0 ? `${stats.avg_rating.toFixed(1)} / 5` : t("reviews.noReviews")}
              {stats.review_count > 0 && ` · ${stats.review_count} ${stats.review_count === 1 ? t("reviews.review") : t("reviews.reviews")}`}
            </span>
          </div>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="luxury-btn-outline"
          data-testid="write-review-btn"
        >
          {showForm ? t("reviews.cancel") : t("reviews.writeReview")}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-[#F5F2ED] p-6 lg:p-8 mb-10 max-w-2xl" data-testid="review-form">
          <div className="mb-5">
            <label className="overline mb-2 block">{t("reviews.yourRating")} *</label>
            <StarRating value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} size="lg" />
          </div>
          {!user && (
            <div className="mb-5">
              <label className="overline mb-2 block">{t("reviews.yourName")}</label>
              <input
                className="luxury-input-box"
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                placeholder={t("reviews.namePlaceholder")}
                data-testid="review-name"
              />
            </div>
          )}
          <div className="mb-5">
            <label className="overline mb-2 block">{t("reviews.titleInput")}</label>
            <input
              className="luxury-input-box"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              maxLength={120}
              data-testid="review-title"
            />
          </div>
          <div className="mb-5">
            <label className="overline mb-2 block">{t("reviews.comment")} *</label>
            <textarea
              className="luxury-input-box"
              rows={4}
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              required
              minLength={3}
              data-testid="review-comment"
            />
          </div>
          <button type="submit" disabled={submitting} className="luxury-btn" data-testid="submit-review">
            {submitting ? "..." : t("reviews.submit")}
          </button>
        </form>
      )}

      {reviews.length === 0 ? (
        <p className="text-[#736B66] py-10">{t("reviews.beTheFirst")}</p>
      ) : (
        <div className="space-y-8 max-w-3xl">
          {reviews.map((r) => (
            <div key={r.id} className="border-b border-[#EBE5DF] pb-8" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-3 mb-2">
                <StarRating value={r.rating} size="sm" />
                {r.verified_buyer && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#D4AF37]">
                    <CheckCircle2 size={12} /> {t("reviews.verifiedBuyer")}
                  </span>
                )}
              </div>
              {r.title && <h3 className="font-serif text-lg mb-1">{r.title}</h3>}
              <p className="text-sm text-[#2C1E16] mb-3 whitespace-pre-line leading-relaxed">{r.comment}</p>
              <p className="text-xs text-[#736B66]">
                {r.customer_name} · {new Date(r.created_at).toLocaleDateString(lang === "el" ? "el-GR" : "en-US")}
              </p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ProductReviews;
